import {
  getCPFFormatado,
  getCNPJFormatado,
  validarCPF,
  validarCNPJ,
  getMascaraMoeda,
  getFloat,
} from 'utils/uteis';
import { formatCurrency } from 'utils/format_currency';

export interface Produto {
  id: string;
  nomeRazao: string;
  documento: string;
  valor: string;
  valorNumerico: number;
}

export interface SecaoAquisicao {
  titulo: string;
  produtosCnpj: Produto[];
  produtosCpf: Produto[];
}

export const getSecaoKey = (secao: SecaoAquisicao, index: number) => {
  return secao.titulo.replace(/\s+/g, '-').toLowerCase() || `secao-${index}`;
};

export const formatarValorInput = (valor: string) => {
  const apenasDigitos = valor.replace(/\D/g, '');
  if (apenasDigitos === '') return '';
  return getMascaraMoeda(parseInt(apenasDigitos, 10) / 100);
};

export const computeTotais = (localSecoes: SecaoAquisicao[]) => {
  const totais = localSecoes.map((secao) => ({
    totalCnpj: secao.produtosCnpj.reduce((sum, p) => sum + p.valorNumerico, 0),
    totalCpf: secao.produtosCpf.reduce((sum, p) => sum + p.valorNumerico, 0),
  }));
  return {
    totaisPorSecao: totais,
    totalGeral: totais.reduce((sum, secao) => sum + secao.totalCnpj + secao.totalCpf, 0),
  };
};

// Legacy alias for tests and older imports
export const calcularTotais = computeTotais;

export const convertToApiFormat = (secao: SecaoAquisicao) => ({
  cnpj: secao.produtosCnpj.map((p) => ({
    nomeRazao: p.nomeRazao,
    cnpj: p.documento.replace(/\D/g, ''),
    valor: p.valorNumerico,
  })),
  cpf: secao.produtosCpf.map((p) => ({
    nomeRazao: p.nomeRazao,
    cpf: p.documento.replace(/\D/g, ''),
    valor: p.valorNumerico,
  })),
});

export const buildPayload = (localSecoes: SecaoAquisicao[]) => ({
  aquisicoes_pnae_federal_detalhamento: localSecoes.length >= 1 ? convertToApiFormat(localSecoes[0]) : null,
  aquisicoes_governo_estado_detalhamento: localSecoes.length >= 2 ? convertToApiFormat(localSecoes[1]) : null,
  aquisicoes_prefeitura_detalhamento: localSecoes.length >= 3 ? convertToApiFormat(localSecoes[2]) : null,
});

export const applyEdicao = (
  localSecoes: SecaoAquisicao[],
  secaoEditando: number,
  tipoEditando: 'cnpj' | 'cpf',
  produtoEditando: Produto,
) => {
  const updatedSecoes = [...localSecoes];
  const produtoAtualizado = {
    ...produtoEditando,
    valorNumerico: produtoEditando.valorNumerico,
    valor: getMascaraMoeda(produtoEditando.valorNumerico),
  } as Produto;

  if (tipoEditando === 'cnpj') {
    updatedSecoes[secaoEditando].produtosCnpj = updatedSecoes[secaoEditando].produtosCnpj.map((p) =>
      p.id === produtoEditando.id ? produtoAtualizado : p,
    );
  } else {
    updatedSecoes[secaoEditando].produtosCpf = updatedSecoes[secaoEditando].produtosCpf.map((p) =>
      p.id === produtoEditando.id ? produtoAtualizado : p,
    );
  }

  return updatedSecoes;
};

// Legacy aliases
export const editarProdutoInSecoes = applyEdicao;

export const confirmarExclusaoHelper = (localSecoes: SecaoAquisicao[], produtoExcluir: string | null) => {
  if (!produtoExcluir) return localSecoes;
  return localSecoes.map((secao) => ({
    ...secao,
    produtosCnpj: secao.produtosCnpj.filter((p) => p.id !== produtoExcluir),
    produtosCpf: secao.produtosCpf.filter((p) => p.id !== produtoExcluir),
  }));
};

export const excluirProdutoFromSecoes = confirmarExclusaoHelper;

export const formatDocumentoValue = (value: string, tipo: 'cnpj' | 'cpf') => {
  const digits = value.replace(/\D/g, '');
  return tipo === 'cnpj' ? getCNPJFormatado(digits) : getCPFFormatado(digits);
};


export const getDocumentHelperTextFn = (tipoEditando: 'cnpj' | 'cpf' | null, produtoEditando: Produto | null) => {
  if (!tipoEditando || !produtoEditando?.documento) return '';
  if (tipoEditando === 'cnpj' && !validarCNPJ(produtoEditando.documento)) return 'CNPJ inválido';
  if (tipoEditando === 'cpf' && !validarCPF(produtoEditando.documento)) return 'CPF inválido';
  return '';
};

export const validateProdutoEdit = (
  produtoEditando: Produto | null,
  tipoEditando: 'cnpj' | 'cpf' | null,
) => {
  if (!produtoEditando || !tipoEditando) return { ok: false, error: 'invalid' };
  if (!produtoEditando.nomeRazao.trim()) return { ok: false, error: 'Nome/Razão Social é obrigatório' };
  if (tipoEditando === 'cnpj' && !validarCNPJ(produtoEditando.documento)) return { ok: false, error: 'CNPJ inválido' };
  if (tipoEditando === 'cpf' && !validarCPF(produtoEditando.documento)) return { ok: false, error: 'CPF inválido' };

  const valorNumerico = getFloat(produtoEditando.valor);
  if (valorNumerico <= 0) return { ok: false, error: 'Valor deve ser maior que zero' };

  return { ok: true, valorNumerico };
};

// Legacy alias placed after function definition to avoid temporal dead zone
// Return the old shape `{ valid, message }` for backward compatibility with tests
export const validarProdutoParaSalvar = (
  produtoEditando: Produto | null,
  tipoEditando: 'cnpj' | 'cpf' | null,
) => {
  const res = validateProdutoEdit(produtoEditando, tipoEditando);
  return {
    valid: !!res.ok,
    message: res.error || '',
    // expose numeric value when present for callers/tests that need it
    valorNumerico: (res as any).valorNumerico,
  };
};

export const formatarValorParaExibicao = (valor: number | string | null | undefined) => {
  if (!valor && valor !== 0) return 'R$ 0,00';

  const numeroValor = typeof valor === 'string' ? parseFloat(valor) : (valor as number);

  if (isNaN(Number(numeroValor))) return 'R$ 0,00';

  return `R$ ${Number(numeroValor).toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

export { formatCurrency };
